<template>
  <div data-page-name="help-slug">
    <nav v-if="!currentSlug || isIndexPage(currentSlug)">
      <ul class="cards">
        <li v-for="{ slug, title } in treeData" :key="slug" class="card">
          <NuxtLink :to="slug" class="title">
            {{ title }}
          </NuxtLink>
        </li>
      </ul>
    </nav>

    <article v-else>
      <h1 v-if="page?.title">{{ page.title }}</h1>

      <div
        v-if="isString(page?.content)"
        class="content"
        v-photo-swipe="{
          caption: isTouchDevice
            ? $t('ArticleMarkup.zoom-caption-pinch')
            : $t('ArticleMarkup.zoom-caption-scroll', { icon: SvgZoomIn }),
        }"
        v-html="page.content"
      />
    </article>
  </div>
</template>

<script setup lang="ts">
// constants
import { API_ENDPOINTS, PATHS, REGEX } from '~/constants';

// directives
import vPhotoSwipe from '~/directives/photoswipe';

// icons
import SvgZoomIn from '~/assets/svg/zoom-in.svg?raw';

// stores
import { useHelpPageStore } from '~/stores/help';

// utils
import { isString, toSafeArray } from '@/utils';

// types
import { PostStatus } from '~/types';
import type { HelpPage, HelpEntityQuery } from '~/types';

const isTouchDevice = computed(
  () => import.meta.client && 'ontouchstart' in document,
);

const {
  public: { api_v2: baseURL },
} = useRuntimeConfig();

const router = useRouter();
const route = useRoute();

const isIndexPage = (slug: string | string[]) => REGEX.HELP_VERSION.test(slug);

const store = useHelpPageStore();
const { setCurrentPageId } = store;
const { currentVersion, withDrafts, treeData } = storeToRefs(store);

const currentSlug = computed(() => {
  const segments = toSafeArray(route.params.slug);

  if (segments.length === 2 && isIndexPage(segments[0])) {
    return segments[1];
  }

  return segments[0];
});

const page = ref<HelpPage | null>(null);

watch(
  currentSlug,
  async slugValue => {
    if (!slugValue || REGEX.HELP_VERSION.test(slugValue)) return;

    const { error, data } = await useFetch<HelpPage>(
      `${API_ENDPOINTS.HELP_PAGES}/${slugValue}`,
      {
        baseURL,
        method: 'GET',
        server: false,
        query: <HelpEntityQuery>{
          version_id: currentVersion.value?.id,
          'statuses[]': withDrafts.value ? [PostStatus.DRAFT] : [],
        },
      },
    );

    if (error.value) {
      router.push(PATHS.HELP.path)
    } else {
      page.value = data.value;
    }
  },
  { immediate: true },
);

watchEffect(() => page.value?.id && setCurrentPageId(page.value.id));

const title = computed(() => {
  let res = 'Object First Help';

  if (page.value?.title) {
    res += `: ${page.value?.title}`;
  }

  return res;
});

useHead({ title });

definePageMeta({
  layout: 'help',
  middleware: defineNuxtRouteMiddleware((to, from) => {
    const { preview } = from.query;

    if (preview && to.query.preview !== preview) {
      return navigateTo({
        path: to.path,
        query: {
          ...to.query,
          preview,
        },
      });
    }
  }),
});

onMounted(() => {
  const scrollContainer = document.querySelector('main');

  if (scrollContainer instanceof HTMLElement) {
    scrollContainer.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
});
</script>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/variables/_color';
@use '_/variables/_font';
@use '_/fn';

$wrapper-width: 80rem;

[data-page-name='help-slug'] {
  article {
    height: 100%;
    padding: 1rem;

    @include size.wrapper(80rem);

    h1 {
      color: fn.token('text-primary');
      font-family: font.$inter;
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 1.75rem;
    }

    &:deep(.content) {
      font-family: font.$inter;
      font-weight: 400;
      font-size: 0.875rem;
      line-height: 1.5rem;
      color: fn.token('text-secondary');

      padding: 2rem 0 6rem;

      img {
        cursor: zoom-in;
        width: 100%;
        max-width: 100%;
        height: auto;
      }

      p,
      ul,
      ol {
        text-align: left;
        text-indent: 0;
        padding: 0;
        margin: 0;
      }

      a {
        color: color.$deep-purple;

        &:hover {
          text-decoration: underline;
        }
      }

      p {
        margin: 0.625rem 0 0.625rem 0.9375rem;
      }

      h2 {
        color: color.$dark-gray;

        font-family: font.$openSans;
        font-size: 1.125rem;
        font-weight: 500;
        line-height: 1.1;

        margin: 1.25rem 0 0.625rem;
      }

      ol {
        margin: 0.625rem 0 0.625rem 0.9375rem;
        list-style-type: decimal;
        list-style-position: inside;

        li {
          p:first-of-type {
            display: inline;
            margin: 0;
            padding: 0;
          }
        }
      }

      ul {
        list-style: none;
        margin: 0 0 0 1.25rem;

        li {
          position: relative;
          padding-left: 0.9375rem;
          margin-bottom: 0.25rem;

          &:before {
            content: '';
            background-color: color.$deep-purple;
            position: absolute;
            top: 0.5rem;
            left: 0;
            width: 0.25rem;
            height: 0.25rem;
            border-radius: 50%;
          }
        }
      }

      span {
        &.image-center-wrapper {
          display: block;
        }
      }

      figure.table {
        width: 100%;
        overflow-x: auto;

        table {
          border-spacing: 0;
          border: 0;

          width: 100%;
          height: auto;
          max-height: max-content;

          font-family: font.$inter;
          font-size: 1.125rem;

          th,
          td {
            box-sizing: border-box;
            padding: 1rem 0.5rem;
            text-align: start;
          }

          thead {
            color: fn.token('text-primary');
            @include typo.subtitle-3;

            tr {
              th {
                border-bottom: 2px solid fn.token('outline-primary');
              }
            }
          }

          tbody {
            th,
            td {
              @include typo.body-3;
              border-bottom: 1px solid fn.token('outline-secondary');
            }
          }
        }

        figcaption {
          @include typo.body-3;
          padding-top: 0.5rem;
          text-align: start;
        }
      }
    }
  }

  nav {
    height: 100%;
    padding: 1rem 0;

    display: flex;
    flex-direction: column;

    font-family: font.$inter;
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 1.5rem;
    color: fn.token('text-secondary');

    p,
    ul,
    ol {
      text-align: left;
      text-indent: 0;
      padding: 0;
      margin: 0;
    }

    a {
      color: fn.token('text-primary');

      &:hover {
        text-decoration: underline;
      }
    }

    h2 {
      color: color.$dark-gray;
      font-family: font.$openSans;
      font-size: 1.125rem;
      font-weight: 500;
      line-height: 1.1;
      margin: 1.25rem 0 0.625rem;
    }

    ul.cards {
      list-style: none;
      padding-bottom: 2rem;

      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-auto-rows: minmax(8rem, auto);
      grid-gap: 1.5rem;

      @include media.tablet {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
      }

      .card {
        display: block;
        padding: 1rem;

        border: 1px solid color.$soft-gray;

        .title {
          font-size: 1.5rem;
          padding-bottom: 1rem;
          display: block;
        }
      }
    }
  }
}
</style>
